import * as React from 'react';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useHistory, useRouteMatch } from 'react-router-dom';
import { generatePath } from "react-router";
import { Size, useSize } from 'react-hook-size';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { Button } from "react-bootstrap";

import _ from "lodash";
import moment from 'moment';

import DeckGL from '@deck.gl/react';
import * as turf from '@turf/turf';
import { NavigationControl, ScaleControl, Popup, Source, Layer} from 'react-map-gl';
import LayerVisibilityControlPanel from '../../fields/components/LayerVisibilityControlPanel';
import ESRI_BASE from '../../../components/Map/styles/esri-base.json';

import { ViewState } from 'react-map-gl-draw/node_modules/@types/react-map-gl';

import { useRasterDataSources } from '../../../hooks/useRasterDataSources';
import { Field, SoilProfile, SoilProfileSet, DataSourceLayerMeta, DataSourceRasterLayer, LayerVisibility } from '../../../types/types';

import InfoDrawer from '../../../components/Elements/InfoDrawer';
import FieldDownload from '../../../features/fields/components/show/FieldDownload';
import ZoomExtentTool from '../../fields/components/ZoomExtentTool';

import { zoomToFeature, zoomToFeatures, createFeatureFromField, createFeatureCollection, getViewport, createCentroidFeatureFromField } from '../../../utils/mapUtils';



import '../../../../../styles/mapbox-gl.css'
import '../../../styles/FieldManager.scss';

import BelowWidget from './BelowWidget';

import { useGetAllFields } from '../../../api/getAllFields';
import { StaticMap, MapContext } from 'react-map-gl';
import SoilProfileSetList from './SoilProfileSetList';

import { IDeckGlLayer, MapLayer } from '../../../deckgl/MapLayer';
import BaseMapLayers from '../../../components/BaseMapLayers';
import { SoilProfileMapLayer } from '../../../deckgl/SoilProfileMapLayer';
import SoilDataSourceLayer from './SoilDataSourceLayer';
import useMapLayers from '../../../hooks/useMapLayers';
import FieldList from '../../../components/FieldList';

import { SETTING_SEASON_YEAR } from "../../../utils/settings";
import { useStore } from "../../../providers/AppProvider";
import { useFieldStore } from '../../fields/providers/FieldProvider';

import SoilInfoWidgetSoilsView from '../../fields/components/show/SoilInfoWidgetSoilsView';
//import SoilInfoWidget from '../../fields/components/show/SoilInfoWidget';

import { 
    NAIP_DATA_SOURCE, 
    ESRI_DATA_SOURCE, 
    MAPBOX_DATA_SOURCE, 
    ESRI_DATA_SOURCE_LABELS,
} from '../../../constants';
import { useLayerStoreSoilsView } from '../../fields/providers/LayerProviderSoilsView';

const FIELD_APP_URL = process.env.REACT_APP_FIELD_MGR_ROOT_URL;

const createPointFeature = (profile: SoilProfile): any => {
    const {geom, ...properties} = profile;
    return {
        type: "Feature",
        "properties": properties,
        "geometry": JSON.parse(geom)
    };
}

export const SoilsView = () => {

    const [ RASTER_DATA_SOURCES,  set_RASTER_DATA_SOURCES ] = useState<DataSourceLayerMeta[]>([{
            id: NAIP_DATA_SOURCE,
            type: 'base',
            name: 'Historical Imagery',
            order: 4,
            visible: false,
            minZoom: 10,
            maxZoom: 24
        },{
            id: MAPBOX_DATA_SOURCE,
            type: 'base',
            name: 'Mapbox Imagery',
            order: 5,
            visible: true,
            minZoom: 0,
            maxZoom: 24
        },{
            id: ESRI_DATA_SOURCE_LABELS,
            type: 'base',
            name: 'Labels',
            order: 0,
            visible: true,
            minZoom: 0,
            maxZoom: 18
        }
    ]);
    const [overlayVisibility, setOverlayVisible] = useState<LayerVisibility[]>([]);
    const [rasterDataSources, setRasterDataSources] = useState<DataSourceRasterLayer[]>([]);
    const [soilRasterDataSources, setSoilRasterDataSources] = useState<DataSourceRasterLayer[]>([]);
    const [soilRasterVisibility, setSoilRasterVisibility] = useState<boolean>(false);
    const [updateSoilsKey, setUpdateSoilsKey] = useState<number>(0);
  
   
    const { dataSources, loading } = useRasterDataSources(RASTER_DATA_SOURCES);
    const [showDownload, setShowDownload] = useState(false);
    const history = useHistory();
    const match = useRouteMatch(`${FIELD_APP_URL}/:vp/soils`);
    const { vp, field } = useParams();

    const mapContainerRef = useRef(null);
    const { width, height } = useSize(mapContainerRef);

    let {data: fields} = useGetAllFields(); // This will get the unfiltered fields
    let { fields: fieldsFiltered } = useFieldStore();

    const { rasterLayers, clearLayers } = useLayerStoreSoilsView();

    // viewport
    const [viewport, setViewport] = useState<ViewState>(getViewport(vp));
    const [loadedViewport, setLoadedViewport] = useState<boolean>(false);

    // used to help refresh the layers
    const [, setRefreshAt] = useState<number>(0);
 
    // points
    const [selectedPushId, setSelectedPushId] = useState<number | null>(null);
    const [pushPointsVisible, setPushPointsVisible] = useState<boolean>(true);
    const [selectedSeason, setSelectedSeason] = useState<string>("2022");

    // fields
    const [hoverFieldId, setHoverFieldId] = useState<number | null>(null);
    const [selectedFieldId, setSelectedFieldId] = useState<number | null>(null);
    const [selectedField, setSelectedField] = useState<Field | null>(null);
    const [selectedFieldIncrement, setSelectedFieldIncrement] = useState<number>(0);
    const [fieldsDataSource, setFieldsDataSource] = useState(null);
    const [centroidDataSource, setCentroidDataSource] = useState(null);

    // Deck.gl
    const { mapLayers, addMapLayer, removeMapLayer, clearMapLayers } = useMapLayers();
    const [selectedSoilProfileSet, setSelectedSoilProfileSet] = useState<SoilProfileSet | null>(null);
    const [selectedSoilProfile, setSelectedSoilProfile] = useState<SoilProfile | null>(null);
    const [hoverSoilProfileIndex, setHoverSoilProfileIndex] = useState<number>(-1);

    const [hoverInfo, setHoverInfo] = useState<any>(null);
    const [size, setSize] = useState<Size>({
        width: width || 800,
        height: height || 800,
    });

    const [rightInfoDrawerOpen, setRightInfoDrawerOpen] = useState<boolean>(true);

    const {settings, orgId, socket, updateFieldSelected, updateLayersForExport, setDownloadDateRange } = useStore();
    
    useEffect(() => {
        console.log("090523 socket", socket);
    }, [socket]);
    
    useEffect(() => {
        if (dataSources){
            console.log("DD012524 RASTER_DATA_SOURCES", RASTER_DATA_SOURCES)
            console.log("DD012524 dataSources", dataSources)
            console.log("DD012524 overlayVisibility", overlayVisibility)
            overlayVisibility.forEach((item) => {
                set_RASTER_DATA_SOURCES(RASTER_DATA_SOURCES.map((itemR) => {
                    //console.log("DD012524 item", item)
                    //console.log("DD012524 itemR", itemR)
                    if (itemR.id === item.layer_id) {
                        itemR.visible = item.is_visible;
                        return itemR;
                    }
                    return itemR;
                }))
            })
            console.log("DD012524 RASTER_DATA_SOURCES", RASTER_DATA_SOURCES)
        }
    }, [ overlayVisibility ]);

    useEffect(() => {
        console.log("110822 settings[SETTING_SEASON_YEAR]", settings[SETTING_SEASON_YEAR]);
        console.log("110822 SETTING_SEASON_YEAR", SETTING_SEASON_YEAR);
        //const dataSources_loading = useSoilHorizonDataSources(horizonSelected);
        //console.log("110822 dataSources_loading", dataSources_loading);
        //dataSources = dataSources_loading.dataSources;
        //loading =  dataSources_loading.loading;
        setSelectedSeason(settings[SETTING_SEASON_YEAR]);
    }, [settings[SETTING_SEASON_YEAR]]);

    useEffect(() => {
        if (!loading && dataSources) {
            const baseSources: DataSourceRasterLayer[] = dataSources.filter((d: DataSourceRasterLayer) => d.type == "base")
            const baseLayers: LayerVisibility[] = baseSources.map((d: DataSourceRasterLayer) => {
                return {
                    layer_id: d.id,
                    layer_name: d.name,
                    is_visible: d.is_visible,
                    order: d.order,
                    source: d.source
                };
            });


            setOverlayVisible(baseLayers);
    
            setRasterDataSources(dataSources);
        }
    }, [dataSources, loading]);

    useEffect(() => {
        if (width && height) {
            setSize({ width, height });
        }
    }, [width, height]);

    useEffect(() => {
        console.log("soilRasterDataSources", soilRasterDataSources);
    }, [soilRasterDataSources]);

    useEffect(() => {
        //console.log(pushPointsVisible)
        if (selectedSoilProfileSet){
            if(pushPointsVisible) {
                addSoilProfileSetLayer(selectedSoilProfileSet);   
            }else{
                removeMapLayer(selectedSoilProfileSet.soilprofilesetId.toString());
            } 
        }
    }, [pushPointsVisible]);

    // set selected field when the field id is in the url
    useEffect(() => {
        console.log("082423 fields", fields);
        console.log("082423 field", field);
        if (fields && field) {
            const selectedField = fields.find(f => f.id == field);
            const presentFieldId = selectedFieldId;
            if (selectedField /*&& width && height*/) {
                setSelectedFieldId(selectedField.id ? selectedField.id : null);
                setSelectedField(selectedField);
            }
        }
    }, [fields]);

    useEffect(() => {
        console.log("090123 selectedField", selectedField)
        console.log("010924 fieldsFiltered", fieldsFiltered);
        console.log("010924 field", field);
        if (fieldsFiltered) {
            const polygonFeatures = fieldsFiltered.map((f: Field) => createFeatureFromField(f));
            const pointFeatures = fieldsFiltered.map((f: Field) => createCentroidFeatureFromField(f));

            const polygonFeatureCollection = createFeatureCollection(polygonFeatures);
            const pointFeatureCollection = createFeatureCollection(pointFeatures);

            setFieldsDataSource(polygonFeatureCollection);
          setCentroidDataSource(pointFeatureCollection);
      }
    }, [fieldsFiltered]);
        
    useEffect(() => {
        console.log("090523 selectedField", selectedField)
         if (selectedField){
             updateFieldSelected(selectedField)
         }
     }, [selectedField]);

     useEffect(() => {
        console.log("090523 soilRasterDataSources", soilRasterDataSources)
        //let naip = overlayVisibility.find((item) => { return item.layer_id === NAIP_DATA_SOURCE });
        //updateLayersForExport(overlayVisibilityMapControl.concat(naip ? naip : []));
        const preppedForClipSoilRasterDataSources = soilRasterDataSources.map((source: DataSourceRasterLayer, sourceIndex:number) => {
            source.is_visible = true;
            return source;
        })
        console.log("090523 preppedForClipSoilRasterDataSources", preppedForClipSoilRasterDataSources)
        console.log("090523 selectedSeason", selectedSeason)
        updateLayersForExport(preppedForClipSoilRasterDataSources);
        if (soilRasterDataSources.length > 0){
            let min_date = selectedSeason ? `${selectedSeason}-01-01` : soilRasterDataSources[0].source.min_date;
            let max_date = selectedSeason ? `${selectedSeason}-12-31` : soilRasterDataSources[0].source.max_date;
            let momentDateMin = moment(min_date).format('YYYY-MM-DD');
            var minDate:any = new Date(momentDateMin);
            var minDaysSinceEpoch = Math.floor(minDate/8.64e7);
            let momentDateMax = moment(max_date).format('YYYY-MM-DD');
            var maxDate:any = new Date(momentDateMax);
            var maxDaysSinceEpoch = Math.floor(maxDate/8.64e7);
            setDownloadDateRange({
                "yearStart": minDaysSinceEpoch,
                "min_date": min_date,
                "yearEnd": maxDaysSinceEpoch,
                "max_date": max_date,
            });
        }
    }, [soilRasterDataSources]);

    // zoom to a field when the field selection changes
    useEffect(() => {
        if (fields && field) {
            const selectedField = fields.find(f => f.id == field);
            const presentFieldId = selectedFieldId;
            if (selectedField && width && height) {
                setSelectedFieldId(selectedField.id ? selectedField.id : null);
                setSelectedField(selectedField);
                setViewport({ ...zoomToFeature(createFeatureFromField(selectedField), viewport, width, height, 85), transitionDuration: 500 });
            }
        }
    }, [field, selectedFieldIncrement]);

    useEffect(() => {
        onSoilProfileSetSelected(selectedSoilProfileSet);
    }, [selectedPushId]);

    const delayedUrlUpdate = React.useCallback(_.debounce((url: any) => history.replace(url), 250), []);
    const onInteractionStateChange = (e: any) => {
        if (!e.isDragging && !e.isPanning && !e.isZooming) {
            const str = `@${viewport.latitude.toFixed(5)},${viewport.longitude.toFixed(5)},${viewport.zoom.toFixed(2)}`;
            const url = field ?
                generatePath(`${FIELD_APP_URL}/:vp/soils/:field`, { vp: str, field: field }) :
                generatePath(`${FIELD_APP_URL}/:vp/soils`, { vp: str });

            delayedUrlUpdate(url);
        }
    }

    const zoomToExtent = React.useCallback(() => {
        if (width && height && fieldsDataSource) {
            deselectField();
            setViewport({ ...zoomToFeatures(fieldsDataSource, viewport, width, height, 100), transitionDuration: 500 });
        }
    }, [fieldsDataSource, viewport, width, height]);

    // field selection and hover
    const onSelectField = React.useCallback((field: Field) => {
        setSelectedFieldIncrement(prev => prev + 1); 
        if (match) {
            history.push(`${match.url}${match.url.endsWith("/") ? "" : "/"}${field.id}`);
        }
    }, []);

    const onHoverField = React.useCallback((field: Field) => {
        setHoverFieldId(field ? field.id! : null);
    }, []);

    const deselectField = () => {
        setSelectedFieldId(null);
        setSelectedField(null);

        if (match) {
            history.push(`${match.url}`);
        }
    };

    const onSoilProfileSetSelected = (profileSet: SoilProfileSet | null) => { 
        // removed the previous layer 
        if(selectedSoilProfileSet) {
            removeMapLayer(selectedSoilProfileSet.soilprofilesetId.toString());
        }
        if(profileSet) {
            addSoilProfileSetLayer(profileSet);
        } 
        setSelectedSoilProfileSet(profileSet ? {...profileSet} as SoilProfileSet : null);
    }

    const addSoilProfileSetLayer = (soilProfileSet: SoilProfileSet) => {
        const pointFeatures = soilProfileSet.soilprofiles.map((profile: SoilProfile) => createPointFeature(profile));

        if(pointFeatures.length) {
            const pointFeatureCollection = createFeatureCollection(pointFeatures);

            const mapLayer = new SoilProfileMapLayer(
                soilProfileSet.soilprofilesetId.toString(), 
                soilProfileSet.name, 
                true, 
                pointFeatureCollection, 
                {},
                selectedPushId,);

            addMapLayer(mapLayer);
        }
    }

    const onSetSelectedSoilProfile = (soilProfile: SoilProfile | null) => {
        // Called when selecting point from list
        console.log("111122 soilProfile", soilProfile)
        setSelectedSoilProfile(soilProfile);
        if(soilProfile) {
            let _layerIndex = selectedSoilProfileSet?.soilprofiles.findIndex((point: any) => point?.name === soilProfile.name)
            if (_layerIndex){
                setSelectedPushId(_layerIndex);
            }
        }
        /* Zoom to point (turned-off for now)
        if(soilProfile) {
            if(width && height) {
                const feature = createPointFeature(soilProfile);
               setViewport({
                    ...zoomToFeature(
                        turf.buffer(feature, 50, {units: 'meters'}), viewport, width, height, 0), 
                        transitionDuration: 200 
                });
            }
        }*/
    }

    const onObjectClick = ({layer, object}: any) => {
        // Called when selecting point from map

        if(!object) {
            //setSelectedSoilProfile(null);
            return;
        }

        if(object.properties.hasOwnProperty('type') && object.properties.type === 'plant') { // plant

        } 

        if(object.properties.hasOwnProperty('name')) { // soil profile
            if(object.properties.name !== selectedSoilProfile?.name) {
                setSelectedSoilProfile({...object.properties} as SoilProfile);
                if(object.properties) {
                    let _layerIndex = selectedSoilProfileSet?.soilprofiles.findIndex((point: any) => point?.name === object.properties.name)
                    if (_layerIndex){
                        setSelectedPushId(_layerIndex);
                    }
                }
            }
        }
        /* Zoom to point (turned-off for now)
        if(object) {
            if(width && height && viewport.zoom < 18) {
                setViewport({...zoomToFeature(
                    turf.buffer(object, 50, {units: 'meters'}), viewport, width, height, 100),
                    transitionDuration: 200 
                });
            }
        }*/
    }

    const onInfoDrawerClose = useCallback(() => {
        clearMapLayers();
        clearLayers();
        deselectField();
        setSelectedSoilProfileSet(null);
        setSelectedSoilProfile(null);
    }, []);

    const onHover = useCallback((event:any, mapLayers:any) => {
        //
        //console.log(event)
        if(event.layer && event.object) {
            const object = event.object;
            const layer = event.layer;
            const id = layer.id;
            const mapLayer = mapLayers.find((layer:any) => layer.id === id);
            if (mapLayer){
                const desc = mapLayer.getDescForObject(object);
                let desc2 = desc.split('\n').map((l: string, index:number) => {
                    return((l == '') ? <></> : <span key={`s-${index}`}>{l}<br></br></span>)
                });
                setHoverInfo({
                    longitude: event.coordinate[0],
                    latitude: event.coordinate[1],
                    fieldName: desc2.filter((r: any) => r.key != null),
                });
            }else{
                setHoverInfo(null);
            }
        }else{
            setHoverInfo(null);
        }

    }, []);

    /*const getTooltip = ({layer, object}: any) => {
        if(layer && object) {
            const { id } = layer;

            const mapLayer = mapLayers.find(layer => layer.id === id);
            if(mapLayer) {
                return mapLayer.getDescForObject(object);
            }
        }
        
        return null;
    }*/

    const infoChildren = useMemo(() => {
        if (!selectedField) {
            return;
        }

        return (
            <BelowWidget soilProfile={selectedSoilProfile} />
        )
    }, [selectedField, selectedSoilProfileSet, selectedSoilProfile]);

    const fieldInfoChildren = useMemo(() => {
        if (!selectedField) {
            return;
        }

        return (
            <> 
                { showDownload && <FieldDownload onCancel={() => setShowDownload(false)} /> }              
                <SoilProfileSetList 
                    field={selectedField} 
                    onSoilProfileSetSelected={onSoilProfileSetSelected}
                    onSoilProfileSelected={onSetSelectedSoilProfile}
                    onSoilProfileHover={((index: number) => setHoverSoilProfileIndex(index))}
                    pushPointsVisible={pushPointsVisible}
                    setPushPointsVisible={setPushPointsVisible}
                />
                <SoilDataSourceLayer 
                    field={selectedField}
                    rampWidth={240}
                    addMapLayer={addMapLayer} 
                    refresh={() => {setRefreshAt(Date.now())}} 
                    fieldId={selectedFieldId}
                    soilRasterDataSources={soilRasterDataSources}
                    setSoilRasterDataSources={setSoilRasterDataSources}
                    setSoilRasterVisibility={setSoilRasterVisibility}
                    soilRasterVisibility={soilRasterVisibility}
                    selectedSeason={selectedSeason}
                    //key={updateSoilsKey}
                    setUpdateSoilsKey={setUpdateSoilsKey}
                />
                <SoilInfoWidgetSoilsView field={selectedField} />
                {/*<SoilInfoWidget field={selectedField} />*/}
            </>
        )
    }, [showDownload, selectedField, selectedSoilProfileSet, width, height, soilRasterDataSources, soilRasterVisibility, selectedSeason]);

    const unorderedLayers = mapLayers.map((layer: MapLayer) => {
        const obj = layer as unknown;
        const provider = obj as IDeckGlLayer;
        return provider.getDeckGlLayer(viewport.zoom);
    });

    const tileLayers =  unorderedLayers.filter((layer: MapLayer) => {
        return layer.constructor.name === "TileLayer";
    });
    const otherLayers =  unorderedLayers.filter((layer: MapLayer) => {
        return layer.constructor.name !== "TileLayer";
    });
    const layers = tileLayers.concat(otherLayers);

    const hoverFieldName = (hoverInfo && hoverInfo.fieldName) || null;

    return (
        <div className="d-flex h-100 position-relative">
            <div className="bg-light border border-bottom-0 border-dark border-left-0 border-top-0 d-flex flex-column flex-shrink-0 rounded-left text-dark" style={{ width: '200px' }}>
                <FieldList
                    title="Soils"
                    fields={fields}
                    fieldsFiltered={fieldsFiltered}
                    selectedId={selectedFieldId}
                    onSelectField={onSelectField}
                    onHoverField={onHoverField}
                />
            </div>

            {selectedField &&
                <div className="bg-light border border-bottom-0 border-left-0 border-right border-secondary border-top-0 d-flex flex-column flex-shrink-0 rounded-right text-dark" style={{ width: '295px' }}>
                    <InfoDrawer 
                        field={selectedField}
                        onClose={onInfoDrawerClose}
                        setShowDownload={setShowDownload}
                    >
                        {fieldInfoChildren}
                    </InfoDrawer>
                </div>
            }

            <div className="flex-fill bg-white position-relative" ref={mapContainerRef}>
                <DeckGL
                    layers={[...layers]}
                    viewState={{
                        ...viewport,
                        maxZoom:25
                    }}
                    controller={true}
                    ContextProvider={MapContext.Provider}
                    onViewStateChange={(v: any) => setViewport(v.viewState)}
                    //getTooltip={getTooltip}
                    onHover={(e:any) => onHover(e,mapLayers)}
                    onClick={(e:any) => onObjectClick(e)}
                    getCursor={({isHovering}:any) => isHovering ? 'pointer' : 'grab'}
                    onInteractionStateChange={onInteractionStateChange}
                    glOptions={{
                        stencil: true,
                        antialias: true
                    }}
                    mapOptions={{
                        antialias: true
                    }}
                >
                    <StaticMap 
                        {...viewport} 
                        width="100%" height="100%" 
                        mapStyle={ESRI_BASE} 
                        preventStyleDiffing={true}
                    >
                        <BaseMapLayers fields={fieldsFiltered ? fieldsFiltered : fields} selectedFieldId={selectedFieldId} hoverFieldId={hoverFieldId} />
                    </StaticMap>
                {rasterDataSources ? _.orderBy(rasterDataSources, ['order'], ['desc']).map((s: DataSourceRasterLayer) => (
                    <Source key={s.uuid} id={s.uuid} type='raster' tileSize={256} tiles={s.tiles}>
                        <Layer
                            key={s.uuid}
                            beforeId="GROUP_baselayer"
                            id={s.uuid}
                            type='raster'
                            source={s.uuid}
                            maxzoom={s.max_zoom} minzoom={s.min_zoom}
                            layout={{ visibility: overlayVisibility.find((item) => { return item.layer_id === s.id })?.is_visible ? 'visible' : 'none' }}
                        />
                    </Source>)
                ) : null}
                {soilRasterDataSources ? _.orderBy(soilRasterDataSources, ['order'], ['desc']).map((s: DataSourceRasterLayer) => (
                    <Source key={s.uuid} id={s.uuid} type='raster' tileSize={256} tiles={s.tiles}>
                        <Layer
                            key={s.uuid}
                            beforeId="GROUP_baselayer"
                            id={s.uuid}
                            type='raster'
                            source={s.uuid}
                            maxzoom={24} //{s.max_zoom} 
                            minzoom={0} //{s.min_zoom}
                            layout={ { visibility: soilRasterVisibility ? 'visible' : 'none'} }
                        />
                    </Source>)
                ) : null}
                    <NavigationControl style={{left: 10, top: 10}} showCompass={false} />
                    <ZoomExtentTool zoomToExtent={zoomToExtent} /> 
                    <ScaleControl style={{right: (selectedSoilProfile && !rightInfoDrawerOpen) ? 50 : 10, top: 15}} maxWidth={200} />
                    <div style={{position: 'absolute', bottom:'unset', top:'47px', right:'10px'}}>
                        <LayerVisibilityControlPanel 
                            key={"soilsLayerVisControl"}
                            showDatePicker={true} 
                            layers={overlayVisibility} 
                            onChange={(visibility: LayerVisibility[]) => setOverlayVisible(visibility)}
                            initiallyOpen={false}
                        />
                    </div>
                    {hoverFieldName && (
                    <Popup
                        key={`popup-${hoverInfo.longitude}`}
                        longitude={hoverInfo.longitude}
                        latitude={hoverInfo.latitude}
                        closeButton={false}
                        className="field-info"
                    >
                        {hoverFieldName}
                    </Popup>)}
                </DeckGL>
            </div>
            { (selectedSoilProfile && rightInfoDrawerOpen)
               ? <div className="bg-light border border-bottom-0 border-top-0 border-right-0 d-flex flex-column flex-shrink-0 rounded-left text-dark" style={{ width: '500px' }}>
                    <InfoDrawer field={null} onClose={() => { setRightInfoDrawerOpen(!rightInfoDrawerOpen) }} showClose={true} side={"right"}>
                        {infoChildren}
                    </InfoDrawer>
                </div>
                : selectedSoilProfile
                    ? <div className="closed-widget-button">
                        <Button 
                            onClick={() => { setRightInfoDrawerOpen(!rightInfoDrawerOpen) }}
                            className="rounded-circle" variant="light" size="sm" title="Close Card" style={{cursor:'pointer', height:'30px', width:'30px'}}>
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </Button>
                    </div>
                    : <></>
            }
        </div>
    );
}
